import { createFileRoute } from "@tanstack/react-router"
import { IconFileText, IconShieldCheck, IconArrowRight, IconHeart, IconAlertCircle } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import {
  Card,
  Button,
  Text,
  Center,
  Box,
  useMantineColorScheme,
  ActionIcon,
  Title,
  Stack,
  Grid,
  Container,
  Group,
  List,
} from "@mantine/core"
import { SEO } from "@/components/seo/SEO"
import { ShareButtons } from "@/components/ui/ShareButtons"
import { StructuredData, createFAQPageSchema, type FAQ } from "@/components/seo/StructuredData"
import { FAQSection } from "@/components/ui/FAQSection"
import { PageCTASection } from "@/components/ui/PageCTASection"

export const Route = createFileRoute("/parents")({
  component: ParentsLandingPage,
})

const parentsFAQs: FAQ[] = [
  {
    question: "How can understanding this case protect my children?",
    answer:
      "The Epstein case reveals systemic patterns of predator behavior, institutional failures, and warning signs that every parent should understand. By studying how predators gain access to children, how they groom victims and families, and how institutions failed to protect children, you can better recognize red flags in your own community. Awareness is the first line of defense. Understanding trafficking tactics, manipulation strategies, and institutional cover-ups empowers you to protect your children and advocate for stronger protections in schools, sports programs, and other organizations.",
  },
  {
    question: "What warning signs should parents watch for?",
    answer:
      "Key warning signs include: (1) Adults who seek unusual amounts of private time with children, (2) Gifts, special privileges, or attention that isolates a child from family, (3) Inappropriate boundary violations (physical touch, personal questions, secrecy), (4) Organizations or programs that lack transparency or parental oversight, (5) Children showing sudden behavioral changes, secrecy, or fear of specific adults or places. The Epstein case shows how predators use wealth, status, and institutional connections to access victims—trust your instincts and maintain open communication with your children.",
  },
  {
    question: "How do I talk to my children about this?",
    answer:
      "Have age-appropriate conversations about body safety, boundaries, and trusted adults. For younger children, focus on 'safe touch' rules and knowing they can always tell you anything. For older children and teens, discuss manipulation tactics predators use: flattery, secrets, gifts, isolation, and grooming behaviors. Emphasize that predators can be powerful, charismatic people—not just 'strangers.' Make it clear that you will always believe and protect them if they report concerning behavior. Create an environment where they feel safe speaking up without fear of getting in trouble.",
  },
  {
    question: "What can parents do to demand accountability?",
    answer:
      "You can protect all children by: (1) Demanding complete file releases to understand institutional failures and prevent future abuse, (2) Supporting legislation that mandates transparency in child trafficking cases, (3) Advocating for stronger child protection policies in schools, sports, and youth organizations, (4) Contacting representatives to demand accountability for all individuals involved—no matter their status or connections, and (5) Teaching your children about predator tactics and ensuring they know they can always come to you. Use the action page to contact your representatives directly.",
  },
  {
    question: "Why is government transparency important for child safety?",
    answer:
      "Government transparency reveals systemic failures that put ALL children at risk. The Epstein case shows how powerful individuals exploited institutional weaknesses to abuse children for decades. If the government hides documents or protects certain individuals, we cannot identify patterns, fix broken systems, or hold enablers accountable. Complete transparency allows parents, advocates, and lawmakers to understand what went wrong and implement reforms to prevent future abuse. The 97% recycled content in recent releases suggests ongoing cover-ups—we demand full disclosure to protect our children.",
  },
]

function ParentsLandingPage() {
  const { colorScheme } = useMantineColorScheme()

  return (
    <>
      <SEO
        title="Protecting Our Children Through Truth"
        description="Every parent's responsibility: understanding trafficking, demanding accountability, and protecting future generations through transparency and awareness."
        canonical="https://rtefn.com/parents"
        keywords="child safety, trafficking awareness, parent advocacy, protecting children, child protection, predator awareness, preventing child abuse, parental responsibility, family safety, keeping kids safe"
        ogType="website"
        ogImage="https://rtefn.com/og-parents.png"
        ogImageAlt="Protecting Our Children - Release The Epstein Files Now"
      />
      <StructuredData data={createFAQPageSchema(parentsFAQs)} />
      <Box className="animate-fade-in">
        {/* Hero Section */}
        <Box
          className={`min-h-screen flex items-center justify-center relative overflow-hidden py-16 ${
            colorScheme === "dark" ? "hero-dark" : "bg-gradient-to-br from-purple-50 via-white to-pink-50"
          }`}
        >
          {colorScheme === "dark" && <Box className="noise-overlay" />}

          <Container size="xl" className="relative z-10">
            <Stack ta="center" maw={1280} mx="auto">
              {/* Tagline */}
              <Box className="animate-fade-in-up" style={{ animationDelay: "0.2s", marginBottom: "var(--space-3xl)" }}>
                <Box
                  component="span"
                  className="inline-block backdrop-blur-md text-lg font-medium rounded-full"
                  style={{
                    letterSpacing: "0.025em",
                    padding: "12px 28px",
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(147, 51, 234, 0.08)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(147, 51, 234, 0.2)",
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(88, 28, 135, 1)",
                  }}
                >
                  Awareness • Protection • Prevention
                </Box>
              </Box>

              {/* Main Headline */}
              <h1
                className="animate-fade-in-up"
                style={{
                  animationDelay: "0.4s",
                  fontFamily: "var(--font-family)",
                  fontWeight: 700,
                  letterSpacing: "-0.04em",
                  lineHeight: 0.95,
                  fontSize: "clamp(3rem, 8vw, 7.5rem)",
                  margin: "0 0 var(--space-2xl) 0",
                  color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a",
                }}
              >
                Protecting Our Children
                <br />
                <span className="gradient-text">Through Truth</span>
              </h1>

              {/* Subtitle */}
              <div className="animate-fade-in-up" style={{ animationDelay: "0.6s" }}>
                <p
                  className="max-w-4xl mx-auto"
                  style={{
                    fontFamily: "var(--font-family)",
                    fontWeight: 400,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.5,
                    fontSize: "clamp(1.25rem, 3vw, 1.75rem)",
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#6b7280",
                    margin: "0 0 var(--space-3xl) 0",
                  }}
                >
                  Knowledge is power. Understanding how trafficking and abuse happen—and demanding accountability—helps
                  protect all children.
                </p>
              </div>

              {/* CTA Buttons */}
              <Box className="animate-fade-in-up" mt="xl" mb="3xl" style={{ animationDelay: "0.8s" }}>
                <PageCTASection contextualAction={{ href: "/documents", label: "Learn More", icon: IconFileText }} />
              </Box>
            </Stack>
          </Container>
        </Box>

        {/* Why This Matters to Parents */}
        <Box
          style={{
            background:
              colorScheme === "dark"
                ? "linear-gradient(180deg, #0a0a0a 0%, #0f0f23 100%)"
                : "linear-gradient(180deg, #ffffff 0%, #faf5ff 100%)",
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
          }}
        >
          <Container size="xl">
            <Box maw={1152} mx="auto">
              <Card
                shadow="lg"
                radius="xl"
                p="3xl"
                style={{
                  background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  border:
                    colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(147, 51, 234, 0.2)",
                }}
              >
                <Stack gap="xl">
                  <Group justify="center" gap="md" align="center">
                    <ActionIcon
                      variant="subtle"
                      size={64}
                      radius="md"
                      style={{ color: colorScheme === "dark" ? "#a855f7" : "#9333ea" }}
                    >
                      <IconHeart size={40} />
                    </ActionIcon>
                    <Title order={2} size="3rem" fw={700}>
                      Every Parent's{" "}
                      <span style={{ color: colorScheme === "dark" ? "#a855f7" : "#9333ea" }}>Responsibility</span>
                    </Title>
                  </Group>

                  <Text size="xl" lh={1.6} maw={900} mx="auto" ta="center">
                    The Jeffrey Epstein case is every parent's nightmare made real. It exposes how powerful individuals
                    can exploit vulnerable young people—and how systems failed to protect them. As parents, we have a
                    responsibility to <strong>understand these dangers</strong>, demand{" "}
                    <strong>complete accountability</strong>, and work to prevent this from happening to any child, ever
                    again.
                  </Text>

                  <Box mt="lg">
                    <Stack gap="md">
                      <Text size="lg" fw={600} ta="center" c="dimmed">
                        How Understanding This Case Protects Children:
                      </Text>
                      <List spacing="md" size="md">
                        <List.Item>
                          <strong>Awareness:</strong> Knowing warning signs and tactics used by predators helps protect
                          our kids
                        </List.Item>
                        <List.Item>
                          <strong>Accountability:</strong> Demanding justice shows children that adults will fight to
                          protect them
                        </List.Item>
                        <List.Item>
                          <strong>Prevention:</strong> Understanding systemic failures helps us fix broken systems
                          before more children are harmed
                        </List.Item>
                        <List.Item>
                          <strong>Empowerment:</strong> Teaching children about safety, boundaries, and speaking up can
                          save lives
                        </List.Item>
                        <List.Item>
                          <strong>Community:</strong> Parents working together create safer environments for all
                          children
                        </List.Item>
                      </List>
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            </Box>
          </Container>
        </Box>

        {/* Key Areas for Parents */}
        <Box
          style={{
            background:
              colorScheme === "dark"
                ? "linear-gradient(180deg, #0f0f23 0%, #1a1a2e 100%)"
                : "linear-gradient(180deg, #faf5ff 0%, #f3e8ff 100%)",
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
          }}
        >
          <Container size="xl">
            <Stack ta="center" mb="4xl">
              <Title order={2} size="3rem" fw={700} mb="xl" ta="center">
                How You Can{" "}
                <span style={{ color: colorScheme === "dark" ? "#a855f7" : "#9333ea" }}>Make a Difference</span>
              </Title>
              <Text size="xl" lh={1.6} maw={900} mx="auto" ta="center" c="dimmed" mb="xl">
                Small actions by informed parents create safer communities for all children
              </Text>
            </Stack>

            <Grid gutter="xl">
              {/* Education & Awareness */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(147, 51, 234, 0.2)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" ? "#9333ea" : "#a855f7",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconAlertCircle size={32} style={{ color: "white" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Stay Informed
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      Understanding the facts—not rumors—helps you recognize warning signs and have age-appropriate
                      conversations with your children about safety and boundaries.
                    </Text>

                    <Button
                      component={Link}
                      to="/documents"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(147, 51, 234, 0.15)" : "rgba(147, 51, 234, 0.08)",
                        color: colorScheme === "dark" ? "#c084fc" : "#9333ea",
                        border: `1px solid ${colorScheme === "dark" ? "rgba(147, 51, 234, 0.3)" : "rgba(147, 51, 234, 0.2)"}`,
                      }}
                    >
                      View Documents
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Demand Accountability */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(147, 51, 234, 0.2)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" ? "#9333ea" : "#a855f7",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconShieldCheck size={32} style={{ color: "white" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Take Action
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      Contact your representatives to demand complete transparency. When parents speak up for children's
                      safety, lawmakers listen. Your voice matters in creating change.
                    </Text>

                    <Button
                      component={Link}
                      to="/action"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(147, 51, 234, 0.15)" : "rgba(147, 51, 234, 0.08)",
                        color: colorScheme === "dark" ? "#c084fc" : "#9333ea",
                        border: `1px solid ${colorScheme === "dark" ? "rgba(147, 51, 234, 0.3)" : "rgba(147, 51, 234, 0.2)"}`,
                      }}
                    >
                      Contact Congress
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Teach Children */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(147, 51, 234, 0.2)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" ? "#9333ea" : "#a855f7",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconFileText size={32} style={{ color: "white" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Spread Awareness
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      Share information with other parents, schools, and community organizations. Building a network of
                      informed, vigilant parents creates safer environments for everyone's children.
                    </Text>

                    <Button
                      component={Link}
                      to="/timeline"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(147, 51, 234, 0.15)" : "rgba(147, 51, 234, 0.08)",
                        color: colorScheme === "dark" ? "#c084fc" : "#9333ea",
                        border: `1px solid ${colorScheme === "dark" ? "rgba(147, 51, 234, 0.3)" : "rgba(147, 51, 234, 0.2)"}`,
                      }}
                    >
                      See Timeline
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Container>
        </Box>

        {/* Important Note About Content */}
        <Box
          style={{
            background:
              colorScheme === "dark"
                ? "linear-gradient(180deg, #1a1a2e 0%, #0f0f23 100%)"
                : "linear-gradient(180deg, #f3e8ff 0%, #ffffff 100%)",
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
          }}
        >
          <Container size="lg">
            <Card
              shadow="md"
              radius="lg"
              p="xl"
              style={{
                background: colorScheme === "dark" ? "rgba(147, 51, 234, 0.1)" : "rgba(147, 51, 234, 0.05)",
                border: `2px solid ${colorScheme === "dark" ? "rgba(147, 51, 234, 0.3)" : "rgba(147, 51, 234, 0.2)"}`,
              }}
            >
              <Stack gap="md">
                <Group>
                  <IconAlertCircle size={24} style={{ color: colorScheme === "dark" ? "#a855f7" : "#9333ea" }} />
                  <Text size="lg" fw={600}>
                    A Note for Parents
                  </Text>
                </Group>
                <Text size="md" lh={1.6} c="dimmed">
                  This site contains court documents and information related to serious crimes. While we believe
                  transparency is essential, some content may not be appropriate for children. We recommend parents
                  review materials before sharing and use this information to have age-appropriate conversations about
                  safety, boundaries, and speaking up when something doesn't feel right.
                </Text>
                <Text size="md" lh={1.6} c="dimmed">
                  Our goal is not to frighten, but to empower. Informed parents raise safer, more aware children who
                  understand that adults should protect them—and that it's never their fault if an adult violates that
                  trust.
                </Text>
              </Stack>
            </Card>
          </Container>
        </Box>

        {/* FAQ Section */}
        <FAQSection
          faqs={parentsFAQs}
          accentColor={colorScheme === "dark" ? "#c084fc" : "#9333ea"}
          title="Frequently Asked Questions"
          subtitle="Answers to common questions about protecting children and demanding accountability"
        />

        {/* Call to Action */}
        <Box
          ta="center"
          style={{
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
            backgroundColor: colorScheme === "dark" ? "#030712" : "#581c87",
          }}
        >
          <Container size="xl">
            <Stack maw={896} mx="auto" ta="center">
              <Title order={2} size="3rem" fw={600} mb="md" c="white">
                Together We Protect Our Children
              </Title>

              <Text size="xl" c="gray.3" mb="xl" maw={768} mx="auto" fw={300} lh={1.6}>
                Every child deserves safety, protection, and justice. When parents demand accountability and
                transparency, we create a safer world for all children—including our own.
              </Text>

              <Stack hiddenFrom="sm" gap="lg">
                <Link
                  to="/action"
                  className="apple-button apple-button-primary text-lg px-8 py-4 min-w-[180px] font-medium bg-white text-gray-900 hover:bg-gray-100"
                >
                  Protect Our Kids
                </Link>
              </Stack>
              <Group visibleFrom="sm" gap="lg" justify="center">
                <Link
                  to="/action"
                  className="apple-button apple-button-primary text-lg px-8 py-4 min-w-[180px] font-medium bg-white text-gray-900 hover:bg-gray-100"
                >
                  Protect Our Kids
                </Link>
              </Group>

              {/* Share Section */}
              <Box mt="2xl">
                <Text size="sm" c="gray.4" mb="sm" ta="center" fw={500}>
                  Share with other parents in your community
                </Text>
                <Group justify="center">
                  <ShareButtons size="lg" variant="filled" />
                </Group>
              </Box>
            </Stack>
          </Container>
        </Box>
      </Box>
    </>
  )
}
